import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, IconButton, Slide } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useOrientation } from '../../hooks/useOrientation';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { useManualTouchScroll, TOUCH_SCROLL_PANEL_SX } from '../../hooks/useManualTouchScroll';
import SonosQueuePanel from './SonosQueuePanel';
import SonosSideSlidePanel from './SonosSideSlidePanel';
import {
  modalSheetHandleBarSx,
  modalSheetHandleSx,
  modalSheetShellSx,
  sonosIconTapSx,
  sonosPanelHeaderSx,
  sonosPanelTitleSx,
} from './sonosUiStyles';
import { RepeatMode } from './sonosPlayMode';
import { SonosMessageHandler } from './sonosFeedback';
import type { SonosSpeaker } from '../../services/sonosApi';

export interface SonosQueueOverlayProps {
  open: boolean;
  onClose: () => void;
  speakerId: number | null;
  coordinator?: SonosSpeaker | null;
  canControl?: boolean;
  shuffle?: boolean;
  onShuffleChange?: (value: boolean) => void;
  repeat?: RepeatMode;
  onRepeatChange?: (mode: RepeatMode) => void;
  onPlayError?: SonosMessageHandler;
  /** Si se pasa, reemplaza el panel Sonos (cola local SHC). */
  panelOverride?: React.ReactNode;
}

const SonosQueueOverlay: React.FC<SonosQueueOverlayProps> = ({
  open,
  onClose,
  speakerId,
  coordinator = null,
  canControl = true,
  shuffle,
  onShuffleChange,
  repeat,
  onRepeatChange,
  onPlayError,
  panelOverride,
}) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  // En horizontal el back lo gestiona SonosSideSlidePanel; evitar doble pushState.
  const requestClose = useOverlayBackButton(open && !isHorizontal, onClose, 'sonos-queue-overlay');
  const [isVisible, setIsVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const gripDragRef = useRef<{ y: number; id: number } | null>(null);
  // Mismo scroll manual que playlist / dónde sonar (DnD no bloquea el dedo).
  const sideScrollRef = useManualTouchScroll(open && isHorizontal);
  // Portal vertical: habilitar mientras esté montado (open o animando cierre).
  const verticalScrollRef = useManualTouchScroll(!isHorizontal && (open || isVisible));

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setDragY(0);
    } else {
      const timer = window.setTimeout(() => setIsVisible(false), 300);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  const onGripDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    gripDragRef.current = { y: e.clientY, id: e.pointerId };
    setDragging(true);
  };

  const onGripMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = gripDragRef.current;
    if (!d || d.id !== e.pointerId) return;
    setDragY(Math.max(0, e.clientY - d.y));
  };

  const onGripEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = gripDragRef.current;
    if (!d || d.id !== e.pointerId) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const dy = e.clientY - d.y;
    gripDragRef.current = null;
    setDragging(false);
    if (dy > 80) requestClose();
    else setDragY(0);
  };

  const queuePanel = panelOverride ?? (
    <SonosQueuePanel
      variant="sheet"
      active={open}
      speakerId={speakerId}
      coordinator={coordinator}
      canControl={canControl}
      shuffle={shuffle}
      onShuffleChange={onShuffleChange}
      repeat={repeat}
      onRepeatChange={onRepeatChange}
      onPlayError={onPlayError}
    />
  );

  // Horizontal: mismo recuadro/posición que detalle de playlist; entra desde abajo.
  if (isHorizontal) {
    return (
      <SonosSideSlidePanel
        open={open}
        onClose={onClose}
        title={t('musicSystem.playback.queue')}
        testId="queue-overlay"
        zIndex={1750}
        horizontalSlideDirection="up"
      >
        <Box
          ref={sideScrollRef}
          data-audio-inner-scroll=""
          sx={{
            ...TOUCH_SCROLL_PANEL_SX,
            height: 'auto',
            flex: 1,
            minHeight: 0,
            px: 1.5,
            pb: 1,
            pt: 0.5,
          }}
        >
          {queuePanel}
        </Box>
      </SonosSideSlidePanel>
    );
  }

  // Vertical: hoja desde abajo. Mantener montado mientras anima el cierre (isVisible),
  // pero si open=true mostrar de inmediato (evita frame en negro / titileo).
  if (typeof document === 'undefined' || (!open && !isVisible)) return null;

  return createPortal(
    <Box
      data-audio-no-swipe=""
      data-testid="queue-overlay"
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (!target.closest('[data-modal-content]')) {
          requestClose();
        }
      }}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: '40px',
        zIndex: 1750,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        pointerEvents: 'auto',
      }}
    >
      <Slide
        direction="up"
        in={open}
        timeout={280}
        appear={false}
      >
        <Box
          data-modal-content
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'relative',
            width: '100%',
            height: 'calc(100vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1,
            pointerEvents: 'auto',
            overflow: 'hidden',
            ...(dragY > 0 ? {
              transform: `translateY(${dragY}px)`,
              transition: dragging ? 'none' : 'transform 300ms cubic-bezier(0.22, 0.61, 0.36, 1)',
            } : {}),
            ...modalSheetShellSx,
          }}
        >
          <Box sx={{ ...modalSheetHandleSx, position: 'relative', flexShrink: 0 }}>
            <Box
              data-audio-player-handle=""
              onPointerDown={onGripDown}
              onPointerMove={onGripMove}
              onPointerUp={onGripEnd}
              onPointerCancel={onGripEnd}
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
              }}
            >
              <Box sx={modalSheetHandleBarSx} />
            </Box>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                requestClose();
              }}
              disableRipple
              aria-label={t('common.close') as string}
              sx={{
                position: 'absolute',
                right: 4,
                top: 1,
                color: '#999',
                p: 0.2,
                zIndex: 1,
                ...sonosIconTapSx,
              }}
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

          <Box sx={{ ...sonosPanelHeaderSx, justifyContent: 'center' }}>
            <Typography sx={{
              ...sonosPanelTitleSx,
              flex: 'none',
              width: '100%',
            }}>
              {t('musicSystem.playback.queue')}
            </Typography>
          </Box>

          <Box
            ref={verticalScrollRef}
            data-audio-inner-scroll=""
            sx={{
              ...TOUCH_SCROLL_PANEL_SX,
              px: 1.5,
              pb: 1,
              pt: 0.5,
            }}
          >
            {queuePanel}
          </Box>
        </Box>
      </Slide>
    </Box>,
    document.body,
  );
};

export default SonosQueueOverlay;
